// src/pages/CourseDetailsPage/components/CourseHero.jsx
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useState, useEffect, useCallback } from 'react';
import "../css/CourseHero.css";
import { EnrollToCourse, checkEnrollment } from '../../../api/EnrollmentApis';

function CourseHero({ course, skipEnrollmentCheck = false }) {
    const navigate = useNavigate();
    const { isLoggedIn, user, isLoading: authLoading } = useAuth();

    const [enrollmentData, setEnrollmentData] = useState(null);
    const [enrollmentLoading, setEnrollmentLoading] = useState(!skipEnrollmentCheck);
    const [enrollmentError, setEnrollmentError] = useState(null);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [enrollSuccess, setEnrollSuccess] = useState(false);

    // ---------- PRICING (discount-aware) ----------
    const originalPrice = parseFloat(course?.price) || 0;
    const discountPrice =
        course?.discount_price != null && course.discount_price !== ''
            ? parseFloat(course.discount_price)
            : null;

    const hasDiscount =
        discountPrice != null &&
        discountPrice > 0 &&
        discountPrice < originalPrice;

    const effectivePrice = hasDiscount ? discountPrice : originalPrice;
    const isFree = effectivePrice === 0;
    const savings = hasDiscount ? originalPrice - effectivePrice : 0;
    const percentOff = hasDiscount
        ? Math.round((1 - effectivePrice / originalPrice) * 100)
        : 0;

    // ---------- CHECK ENROLLMENT STATUS ON COMPONENT MOUNT ----------
    useEffect(() => {
        if (skipEnrollmentCheck) {
            setEnrollmentLoading(false);
            return;
        }

        if (authLoading) {
            return;
        }

        const checkUserEnrollment = async () => {
            if (isLoggedIn && course?.slug) {
                try {
                    setEnrollmentLoading(true);
                    setEnrollmentError(null);
                    const response = await checkEnrollment(course.slug);
                    setEnrollmentData(response?.data ?? null);
                } catch (error) {
                    setEnrollmentError('Failed to check enrollment status');
                    setEnrollmentData(null);
                } finally {
                    setEnrollmentLoading(false);
                }
            } else {
                setEnrollmentLoading(false);
            }
        };

        checkUserEnrollment();
    }, [isLoggedIn, course?.slug, authLoading, skipEnrollmentCheck]);

    const isEnrolled = enrollmentData?.is_enrolled || false;

    const smoothRedirect = useCallback((path) => {
        setIsRedirecting(true);
        setTimeout(() => {
            navigate(path);
        }, 400);
    }, [navigate]);

    // Handle successful enrollment → auto redirect to /content/:slug
    useEffect(() => {
        if (enrollSuccess && course?.slug) {
            const timer = setTimeout(() => {
                smoothRedirect(`/content/${course.slug}`);
            }, 1200);

            return () => clearTimeout(timer);
        }
    }, [enrollSuccess, course?.slug, smoothRedirect]);

    // ---------- PRICING RENDER ----------
    const renderPricing = () => {
        if (isFree) {
            return <span className="coursehero-free-price">Free</span>;
        }

        if (hasDiscount) {
            return (
                <div className="coursehero-price-stack">
                    <span className="coursehero-current-price">
                        ${effectivePrice.toFixed(2)}
                    </span>
                    <span className="coursehero-original-price">
                        ${originalPrice.toFixed(2)}
                    </span>
                    <span className="coursehero-discount-badge">
                        {percentOff}% OFF
                    </span>
                </div>
            );
        }

        return (
            <span className="coursehero-current-price">
                ${originalPrice.toFixed(2)}
            </span>
        );
    };

    const handleEnroll = async () => {
        if (isEnrolled) {
            smoothRedirect(`/content/${course.slug}`);
            return;
        }

        if (!isLoggedIn) {
            localStorage.setItem('enrollment_redirect', JSON.stringify({
                courseSlug: course.slug,
                courseTitle: course.title,
                isFree: isFree,
                effectivePrice: effectivePrice,
                originalPrice: originalPrice,
                hasDiscount: hasDiscount,
                timestamp: new Date().getTime()
            }));
            smoothRedirect('/login');
            return;
        }

        if (isFree) {
            try {
                setEnrollmentLoading(true);
                const response = await EnrollToCourse(course.slug);
                if (response?.data?.success) {
                    localStorage.removeItem('enrollment_redirect');
                    setEnrollmentData({
                        is_enrolled: true,
                        enrollment_id: response.data.enrollment_id,
                        enrolled_at: new Date().toISOString()
                    });
                    setEnrollSuccess(true);
                    setEnrollmentLoading(false);
                }
            } catch (error) {
                setEnrollmentLoading(false);

                // Robust "already enrolled" check — covers both admin and student message
                if (error.response?.data?.message?.toLowerCase().includes('already enrolled')) {
                    setEnrollmentData({
                        is_enrolled: true,
                        enrollment_id: null,
                        enrolled_at: new Date().toISOString()
                    });
                    smoothRedirect(`/content/${course.slug}`);
                } else {
                    alert(error.response?.data?.message || 'Enrollment failed. Please try again.');
                }
            }
        } else {
            smoothRedirect(`/course/${course.slug}/support`);
        }
    };

    const getButtonConfig = () => {
        if (isRedirecting) {
            return {
                text: 'Redirecting...',
                className: 'coursehero-redirecting-btn',
                disabled: true,
                icon: 'fas fa-spinner fa-spin'
            };
        }

        if (enrollSuccess) {
            return {
                text: 'Enrollment Successful!',
                className: 'coursehero-success-btn',
                disabled: true,
                icon: 'fas fa-check-circle'
            };
        }

        if (authLoading || enrollmentLoading) {
            return {
                text: 'Checking...',
                className: 'coursehero-loading-btn',
                disabled: true,
                icon: 'fas fa-spinner fa-spin'
            };
        }

        if (isEnrolled) {
            return {
                text: 'Continue Learning',
                className: 'coursehero-continue-btn',
                disabled: false,
                icon: 'fas fa-play-circle'
            };
        }

        if (isFree) {
            return {
                text: 'Enroll for Free',
                className: 'coursehero-free-btn',
                disabled: false,
                icon: 'fas fa-arrow-right'
            };
        }

        return {
            text: hasDiscount
                ? `Buy Now — $${effectivePrice.toFixed(2)}`
                : 'Purchase Course',
            className: 'coursehero-paid-btn',
            disabled: false,
            icon: 'fas fa-shopping-cart'
        };
    };

    const buttonConfig = getButtonConfig();

    if (authLoading) {
        return (
            <div className="coursehero-compact-hero">
                <div className="coursehero-hero-content">
                    <div className="coursehero-loading">
                        <div className="coursehero-loading-spinner"></div>
                        <p>Checking authentication...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!course) {
        return (
            <div className="coursehero-compact-hero">
                <div className="coursehero-hero-content">
                    <div className="coursehero-loading">
                        <div className="coursehero-loading-spinner"></div>
                        <p>Loading course...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`coursehero-compact-hero ${isRedirecting ? 'coursehero-redirecting' : ''} ${enrollSuccess ? 'coursehero-success' : ''}`}>
            <div className="coursehero-hero-content">
                <div className="coursehero-breadcrumb">
                    <Link to="/courses" className="coursehero-back-link">
                        <i className="fas fa-arrow-left"></i>
                        All Courses
                    </Link>
                    <span className="coursehero-breadcrumb-divider">/</span>
                    <span className="coursehero-category">{course.category || 'Development'}</span>
                </div>

                <div className="coursehero-main-content">
                    <div className="coursehero-text-content">
                        <h1 className="coursehero-title">{course.title}</h1>
                        <p className="coursehero-description">{course.short_description}</p>

                        <div className="coursehero-meta-info">
                            <div className="coursehero-meta-item">
                                <i className="fas fa-signal"></i>
                                <span>
                                    {course.level
                                        ? course.level.charAt(0).toUpperCase() + course.level.slice(1)
                                        : 'Beginner'}
                                </span>
                            </div>
                            <div className="coursehero-meta-item">
                                <i className="fas fa-clock"></i>
                                <span>{course.duration_display || '—'}</span>
                            </div>
                            <div className="coursehero-meta-item">
                                <i className="fas fa-play-circle"></i>
                                <span>{course.total_lessons || '0'} lessons</span>
                            </div>
                            {course.certificate_available && (
                                <div className="coursehero-meta-item">
                                    <i className="fas fa-award"></i>
                                    <span>Certificate</span>
                                </div>
                            )}
                            {hasDiscount && (
                                <div className="coursehero-meta-item coursehero-savings">
                                    <i className="fas fa-tag"></i>
                                    <span>Save ${savings.toFixed(2)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="coursehero-action-card">
                        {enrollSuccess && (
                            <div className="coursehero-success-message">
                                <i className="fas fa-check-circle"></i>
                                <div className="coursehero-success-content">
                                    <h4>Enrollment Successful!</h4>
                                    <p>Redirecting you to the course...</p>
                                </div>
                            </div>
                        )}

                        <div className="coursehero-pricing-section">
                            {renderPricing()}
                        </div>

                        <button
                            className={`coursehero-enroll-btn ${buttonConfig.className}`}
                            onClick={handleEnroll}
                            disabled={buttonConfig.disabled}
                        >
                            {buttonConfig.text}
                            <i className={buttonConfig.icon}></i>
                        </button>

                        <div className="coursehero-features">
                            <div className="coursehero-feature">
                                <i className="fas fa-check"></i>
                                <span>Lifetime Access</span>
                            </div>
                            <div className="coursehero-feature">
                                <i className="fas fa-check"></i>
                                <span>Project Files</span>
                            </div>
                            <div className="coursehero-feature">
                                <i className="fas fa-check"></i>
                                <span>Certificate of Completion</span>
                            </div>
                            {isFree && (
                                <div className="coursehero-feature coursehero-feature-highlight">
                                    <i className="fas fa-check"></i>
                                    <span>Free Forever</span>
                                </div>
                            )}
                            {course.certificate_available && (
                                <div className="coursehero-feature coursehero-feature-highlight">
                                    <i className="fas fa-award"></i>
                                    <span>Certificate Available</span>
                                </div>
                            )}
                        </div>

                        {isLoggedIn && !skipEnrollmentCheck && (
                            <div className="coursehero-enrollment-status">
                                {enrollmentLoading ? (
                                    <div className="coursehero-status-loading">
                                        <i className="fas fa-spinner fa-spin"></i>
                                        <span>Checking enrollment status...</span>
                                    </div>
                                ) : enrollmentError ? (
                                    <div className="coursehero-status-error">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        <span>{enrollmentError}</span>
                                    </div>
                                ) : isEnrolled ? (
                                    <div className="coursehero-status-enrolled">
                                        <i className="fas fa-check-circle"></i>
                                        <span>You're enrolled in this course</span>
                                    </div>
                                ) : (
                                    <div className="coursehero-status-not-enrolled">
                                        <i className="fas fa-info-circle"></i>
                                        <span>You're not enrolled yet</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="coursehero-auth-info">
                            {!isLoggedIn ? (
                                <div className="coursehero-auth-notice">
                                    <i className="fas fa-info-circle"></i>
                                    <span>Login to enroll in this course</span>
                                </div>
                            ) : (
                                <div className="coursehero-user-info">
                                    <i className="fas fa-user-check"></i>
                                    <span>Logged in as {user?.username}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CourseHero;